import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { PaperPlaneTilt, ArrowClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const ContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  useEffect(() => { fetchContacts(); }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getContacts();
      setContacts(res.data);
    } catch (err) {
      logger.error('Failed to load contacts:', err);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setLastSyncResult(null);
    try {
      const res = await adminAPI.syncContactsToMailchimp();
      setLastSyncResult(res.data);
      const { success, failed, total } = res.data;
      if (failed === 0) toast.success(`Synced ${success}/${total} contacts to Mailchimp`);
      else toast.error(`Sync completed with issues: ${success} ok, ${failed} failed`);
    } catch (err) {
      logger.error('Sync failed:', err);
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="bg-white border border-border p-6" data-testid="contacts-tab">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-3">
        <div>
          <h3 className="text-xl font-bold text-primary" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Contacts ({contacts.length})
          </h3>
          <p className="text-xs text-text_muted mt-1">Every registered user. Click "Sync to Mailchimp" to push all contacts into your audience list.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchContacts}
            disabled={loading}
            className="flex items-center space-x-2 px-3 py-2 border border-border rounded text-sm hover:bg-gray-50 disabled:opacity-50"
            data-testid="refresh-contacts-btn"
            title="Refresh"
          >
            <ArrowClockwise size={18} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={handleSync}
            disabled={syncing || contacts.length === 0}
            className="flex items-center space-x-2 bg-accent text-white px-4 py-2 rounded font-medium hover:bg-opacity-90 disabled:opacity-50"
            data-testid="sync-mailchimp-btn"
          >
            <PaperPlaneTilt size={18} weight="bold" />
            <span>{syncing ? 'Syncing...' : 'Sync to Mailchimp'}</span>
          </button>
        </div>
      </div>

      {lastSyncResult && (
        <div className="mb-4 p-3 border border-border bg-gray-50 text-sm" data-testid="sync-result">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-green-700">
              <CheckCircle size={18} weight="fill" />
              <span className="font-bold">{lastSyncResult.success}</span> synced
            </div>
            {lastSyncResult.failed > 0 && (
              <div className="flex items-center gap-1 text-red-700">
                <XCircle size={18} weight="fill" />
                <span className="font-bold">{lastSyncResult.failed}</span> failed
              </div>
            )}
            <span className="text-text_muted">of {lastSyncResult.total} total</span>
          </div>
          {lastSyncResult.failed > 0 && (
            <ul className="mt-2 pl-6 list-disc text-xs text-red-700 max-h-32 overflow-y-auto">
              {lastSyncResult.results.filter(r => !r.success).slice(0, 20).map((r, i) => (
                <li key={i}><span className="font-mono">{r.email}</span> — {r.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-text_muted text-center py-8">Loading contacts...</p>
      ) : contacts.length === 0 ? (
        <p className="text-text_muted text-center py-8">No contacts yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-gray-50">
                <th className="text-left py-2 px-3 font-bold text-primary">Name</th>
                <th className="text-left py-2 px-3 font-bold text-primary">Email</th>
                <th className="text-left py-2 px-3 font-bold text-primary">Role</th>
                <th className="text-left py-2 px-3 font-bold text-primary">Signed up</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.user_id} className="border-b border-border hover:bg-gray-50" data-testid={`contact-row-${c.user_id}`}>
                  <td className="py-2 px-3">{c.name || '—'}</td>
                  <td className="py-2 px-3 font-mono text-xs">{c.email}</td>
                  <td className="py-2 px-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${c.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-blue-50 text-blue-700'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {c.role || 'customer'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-text_muted">{formatDate(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContactsTab;
